/**
 * MoonPhaseComputing — MP2 screen of the Moon Phase Soulmate flow.
 *
 * Route: /flow/moon-phase-soulmate/computing
 *
 * Behavior:
 *   - Fires mutate() once on mount via useEffect.
 *   - While isPending: DualArcRing + MoonOrb animation + caption.
 *   - On success: navigates to /flow/moon-phase-soulmate/reveal with
 *     { runId, output } in React Router location state.
 *   - On BirthDataRequiredError: navigates to /onboarding/birth.
 *   - On other error: shows error message + Retry button.
 *
 * prefers-reduced-motion is respected via MoonOrb.module.css.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../brand/CosmosBackdrop';
import { MoonOrb } from '../../brand/MoonOrb';
import { useRunMoonPhase, BirthDataRequiredError } from '../../../hooks/useRunMoonPhase';
import styles from './MoonPhase.module.css';
import orbStyles from '../../brand/MoonOrb.module.css';

// --------------------------------------------------------------------------
// DualArcRing — concentric spinning arc decoration around MoonOrb
// --------------------------------------------------------------------------

function DualArcRing(): JSX.Element {
  return (
    <svg
      width={220}
      height={220}
      viewBox="0 0 220 220"
      aria-hidden="true"
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      {/* Silver arc — fast spin */}
      <circle
        cx={110}
        cy={110}
        r={105}
        fill="none"
        stroke="var(--silver)"
        strokeWidth="1.5"
        strokeDasharray="849 283"
        className={orbStyles.arcSpin}
      />

      {/* Gold arc — reverse spin */}
      <circle
        cx={110}
        cy={110}
        r={105}
        fill="none"
        stroke="var(--gold)"
        strokeWidth="1.5"
        strokeDasharray="283 849"
        className={orbStyles.arcSpinGold}
      />

      {/* Inner dashed orbit ring */}
      <circle
        cx={110}
        cy={110}
        r={88}
        fill="none"
        stroke="var(--border-soft)"
        strokeWidth="0.5"
        strokeDasharray="4 8"
        className={orbStyles.orbitRing}
      />
    </svg>
  );
}

// --------------------------------------------------------------------------
// MoonPhaseComputing
// --------------------------------------------------------------------------

export function MoonPhaseComputing(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['flows', 'common']);
  const { mutate, data, isError, error } = useRunMoonPhase();

  // Track whether we've already fired the mutation to avoid double-fire
  const hasFired = useRef(false);

  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;
    mutate();
  }, [mutate]);

  // Navigate on success
  useEffect(() => {
    if (!data) return;
    void navigate('/flow/moon-phase-soulmate/reveal', {
      state: { runId: data.run.id, output: data.run.output },
      replace: false,
    });
  }, [data, navigate]);

  // Navigate on birth_data_required
  useEffect(() => {
    if (!isError || !error) return;
    if (error instanceof BirthDataRequiredError) {
      void navigate('/onboarding/birth', {
        state: { hint: 'birth_data_required' },
        replace: false,
      });
    }
  }, [isError, error, navigate]);

  const isGenericError = isError && !(error instanceof BirthDataRequiredError);

  return (
    <div
      className={styles.root}
      data-screen-label="mp-02 Computing lunar match"
    >
      <CosmosBackdrop />

      <main
        role="main"
        aria-label={t('moonPhase.computing.mainAria')}
        className={styles.content}
      >
        {/* Header eyebrow */}
        <div className={styles.headerRow}>
          <div style={{ width: 44 }} aria-hidden="true" />
          <span className={styles.eyebrow} data-testid="mp-computing-eyebrow">
            {t('moonPhase.computing.eyebrow')}
          </span>
          <div style={{ width: 44 }} aria-hidden="true" />
        </div>

        {/* Main content */}
        {!isGenericError && (
          <div className={styles.computingCenter} aria-live="polite" aria-atomic="true">
            <div
              className={styles.sigilWrap}
              data-testid="mp-computing-sigil"
              style={{ position: 'relative', width: 220, height: 220 }}
            >
              {/* Outer spinning arcs */}
              <DualArcRing />

              {/* Moon orb centered */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <MoonOrb phase={0.5} size={100} />
              </div>
            </div>

            <p className={styles.computingCaption} data-testid="mp-computing-caption">
              {t('moonPhase.computing.caption')}
            </p>
          </div>
        )}

        {/* Generic error state */}
        {isGenericError && (
          <div
            className={styles.errorBlock}
            role="alert"
            data-testid="mp-computing-error"
          >
            <p className={styles.errorMessage}>
              {error?.message ?? t('moonPhase.computing.errorFallback')}
            </p>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => mutate()}
              data-testid="mp-computing-retry-btn"
            >
              {t('common:actions.retry')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
